import { Alert, Button, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../../api/client';
import { errorCopy } from '../../api/errors';
import { listKey } from '../../api/queryKeys';
import { useIslandToday, useLocationId } from '../../hooks/useSeedLocation';
import { notifyDone, notifyError } from '../../lib/notify';

/**
 * The menu, off this system and onto something the owner holds.
 *
 * This is the oldest open item on the status page and the cheapest to close. **The menu lives in one
 * database and nowhere else** — nineteen categories, a hundred and thirty-seven dishes and two
 * hundred and ninety-nine sizes, with their prices, their tax sets and what each one is made of.
 * There is no export anywhere in this portal, so losing that database means all of it is typed in
 * again by hand, from the printed menu and from memory, and the prices are the half nobody can
 * reconstruct.
 *
 * The document behind this button already exists and always did: publishing freezes the whole
 * sellable menu into an immutable snapshot so that a register which has been offline all evening prices
 * a line against exactly the menu the server will price it against. That is the same document, read
 * from the office. It had no caller.
 *
 * ## Why it also answers a question the bar asks weekly
 *
 * "This price is wrong on the register." That is two different faults with two different fixes — the
 * menu on the terminal is out of date, or the price in the office was never right — and the only way
 * to tell them apart is to look at WHAT WAS SENT rather than at what is on the edit screens now. The
 * counts below are counted out of the frozen document, not out of the live tables, so they answer
 * that question rather than the easier one beside it.
 *
 * ## Why it is a file and not a screen
 *
 * A backup that lives on this site is not a backup. The point is a copy the owner has somewhere
 * else, and the version number and the date are in the filename so that two of them can be told
 * apart a year later without opening either.
 */

const SNAPSHOT_PATH = '/admin/v1/catalog/snapshot';

/**
 * Exported, and the reason is a bug this had on its first run.
 *
 * Sending the menu creates the very document this panel reads, and the panel went on saying
 * "nothing has been sent to the registers yet" underneath a green "Menu sent" — the exact failure
 * `api/queryKeys.ts` exists to prevent, where one caller invalidates a key another caller is not
 * reading. So the key travels with the panel rather than being written out a second time on the
 * screen that publishes. Derive it from the request; then two callers of one endpoint cannot
 * disagree about what to throw away.
 */
export const snapshotKey = (locationId: string | undefined) => listKey(SNAPSHOT_PATH, { locationId });

/** The collections worth counting on screen. The document carries sixteen; these are the menu. */
const COUNTED: { key: string; one: string; many: string }[] = [
  { key: 'categories', one: 'category', many: 'categories' },
  { key: 'items', one: 'dish or drink', many: 'dishes and drinks' },
  { key: 'variants', one: 'size', many: 'sizes' },
  { key: 'modifierOptions', one: 'choice or extra', many: 'choices and extras' },
  { key: 'bundles', one: 'combo', many: 'combos' },
  { key: 'priceEntries', one: 'price', many: 'prices' },
];

interface Snapshot {
  version: number;
  summary: string | null;
  collections: Record<string, unknown[]>;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function SaveTheMenu() {
  const locationId = useLocationId();
  // The date on the bar's own calendar, never the reader's browser — the file is named after the
  // day it was taken, and a copy saved at seven in the evening must not be stamped tomorrow.
  const today = useIslandToday();

  const snapshot = useQuery({
    queryKey: snapshotKey(locationId),
    queryFn: () =>
      rawApi<Snapshot>('GET', `${SNAPSHOT_PATH}?locationId=${encodeURIComponent(locationId ?? '')}`),
    enabled: Boolean(locationId),
    // A menu is republished by hand and rarely; refetching it on every focus would pull the whole
    // document down again for a screen that is mostly read once.
    staleTime: 60_000,
    retry: false,
  });

  const doc = snapshot.data;
  // 404 is the honest answer for a bar that has never pressed Send, and it is not an error to
  // report as one — there is genuinely nothing to save yet.
  const neverPublished = snapshot.isError && (snapshot.error as { status?: number })?.status === 404;
  const failure =
    snapshot.isError && !neverPublished ? errorCopy(snapshot.error, 'read the sent menu') : null;

  const save = () => {
    if (!doc) return;
    try {
      const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `Deco Stop menu ${today} (version ${doc.version}).json`;
      anchor.click();
      // Long enough for the browser to have taken the bytes; the object would otherwise be held
      // for the life of the page every time somebody saves a copy.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      notifyDone('Menu saved', 'Keep it somewhere that is not this system.');
    } catch (err) {
      notifyError(err, 'save a copy of the menu');
    }
  };

  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <Title order={5}>Keep a copy of the menu</Title>
          <Text size="sm" c="dimmed" mt={2}>
            Exactly what the registers were last sent — every category, dish, size, choice, combo and price,
            as it was at the moment you sent it. Save one after any change worth keeping. If this system is
            ever lost, this file is the difference between restoring the menu and typing it in again.
          </Text>
        </div>
        <Button variant="default" disabled={!doc} onClick={save}>
          Save a copy
        </Button>
      </Group>

      {failure && (
        <Alert color="red" title={failure.title} mt="sm">
          {failure.message}
        </Alert>
      )}

      {neverPublished && (
        <Alert color="yellow" variant="light" mt="sm" title="Nothing has been sent to the registers yet">
          <Text size="sm">
            There is no menu to save until you send one. Use “Send menu to registers” above — that is what
            freezes the copy this button hands back.
          </Text>
        </Alert>
      )}

      {doc && (
        <Stack gap="xs" mt="sm">
          {/* WHEN it was sent is under the page title already and is deliberately not repeated
              here: one fact written down in two places is this repository's own favourite defect,
              and these two would be read side by side. What is not up there is WHAT was sent. */}
          <Text size="sm" fw={500}>
            What the registers are working from{doc.summary ? ` — ${doc.summary}` : ''}
          </Text>
          {/* Counted out of the frozen document rather than the live tables, which is the whole
              value of showing them: "137 dishes" here and 138 on the Items tab is the answer to
              "why is that new dish not on the register", stated without anybody having to ask. */}
          <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }}>
            {COUNTED.map((c) => (
              <Text key={c.key} size="sm">
                {plural(doc.collections?.[c.key]?.length ?? 0, c.one, c.many)}
              </Text>
            ))}
          </SimpleGrid>
        </Stack>
      )}
    </Paper>
  );
}
